import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Library } from "lucide-react";
import { api, fetchPulse, fetchPulseMeta, fetchPulseItem } from "../lib/api";
import { timeAgo } from "../lib/tone";
import { safeUrl } from "../lib/safeUrl";
import { PULSE_TO_CAUSE, HERO_HOME } from "../lib/volunteerVisuals";
import { PageHero, CategoryChips, ProvinceSelect, SearchInput } from "../components/community/PageParts";
import { RelatedActions } from "../components/community/RelatedActions";
import { ShareMenu } from "../components/ShareMenu";

const ACTION_LABEL = { volunteer: "Volunteer", donate: "Donate", fundraise: "Fundraise", attend: "Attend", mentor: "Mentor", foster: "Foster",
  support: "Support", sponsor: "Sponsor", participate: "Participate", join: "Join" };

const summaries = {};
const useSummary = (category, province) => {
  const key = `${category}|${province || ""}`;
  const [v, setV] = useState(null);
  useEffect(() => {
    summaries[key] = summaries[key] || api.get("/participation/summary", { params: { category, province: province || undefined } }).then((r) => r.data);
    summaries[key].then(setV).catch(() => {});
  }, [key, category, province]);
  return v;
};

const PulseCard = ({ s, province, open: initial = false }) => {
  const [open, setOpen] = useState(initial);
  const cause = PULSE_TO_CAUSE[s.pulse_category] || "Community Events";
  const part = useSummary(cause, province || s.provinces?.[0]);
  return (
    <article className={`rounded-3xl border bg-white p-5 sm:p-6 ${initial ? "border-emerald-400 ring-2 ring-emerald-100" : "border-slate-200"}`} data-testid={`pulse-card-${s.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <span className="rounded-full bg-[#1E3A8A]/10 px-2.5 py-1 font-semibold text-[#1E3A8A]" data-testid="pulse-card-category">{s.pulse_category}</span>
        <span>{s.outlet}</span><span>· {timeAgo(s.published)}</span>{s.provinces?.length > 0 && <span>· {s.provinces.join(", ")}</span>}
      </div>
      <a href={safeUrl(s.link)} target="_blank" rel="noopener noreferrer" className="mt-3 block text-lg font-semibold leading-snug text-slate-900 hover:text-[#1E3A8A]" data-testid="pulse-card-title">
        {s.title} <ArrowUpRight size={15} className="inline text-slate-400" />
      </a>
      {s.action_note && <p className="mt-2 text-sm text-slate-800" data-testid="pulse-card-action-note"><span className="font-semibold text-emerald-700">What can I do? </span>{s.action_note}</p>}
      <p className="mt-3 text-xs text-slate-600" data-testid={`pulse-participation-${s.id}`}>Community participation in {cause}: <b className="text-slate-800">Interested {part?.interested ?? "–"}</b> · <b className="text-slate-800">Attended {part?.attended ?? "–"}</b></p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-slate-600">Actions available:</span>
        {s.actions?.map((a) => <span key={a} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800" data-testid={`pulse-action-${a}`}>{ACTION_LABEL[a]}</span>)}
        <span className="ml-auto flex items-center gap-2">
          <ShareMenu path={`/pulse?story=${s.id}`} title={s.title} kind="story" testId={`pulse-share-${s.id}`} />
          <button onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`pulse-help-toggle-${s.id}`} className="btn-green inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold">
            How You Can Help <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </span>
      </div>
      {open && <div className="mt-4"><RelatedActions cause={cause} province={province || s.provinces?.[0]} /></div>}
    </article>
  );
};

export default function Pulse() {
  const [params] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ category: params.get("category") || "", province: params.get("province") || "", q: "" });
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState(20);
  const [shared, setShared] = useState(null);
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setLimit(20); };

  useEffect(() => { fetchPulseMeta().then(setMeta).catch(() => {}); }, []);
  useEffect(() => { const id = params.get("story"); if (id) fetchPulseItem(id).then(setShared).catch(() => setShared(null)); }, [params]);
  useEffect(() => {
    const t = setTimeout(() => fetchPulse({ ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), limit }).then(setData).catch(() => setData({ items: [], total: 0 })), 250);
    return () => clearTimeout(t);
  }, [f, limit]);

  return (
    <div data-testid="pulse-page">
      <PageHero eyebrow="Community Pulse™" title="Community Pulse" img={HERO_HOME} alt="Neighbours volunteering together" testId="pulse-hero"
        sub="Discover what's happening in your community and how you can help." />
      <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="flex flex-wrap gap-3">
          <SearchInput value={f.q} onChange={(v) => set("q", v)} placeholder="Search community stories" testId="pulse-search" />
          <ProvinceSelect value={f.province} onChange={(v) => set("province", v)} testId="pulse-province" />
          <Link to="/news" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:border-[#1E3A8A]" data-testid="pulse-sources-link"><Library size={16} />Sources & media tools</Link>
        </div>
        <div className="mt-4"><CategoryChips cats={meta?.categories || []} value={f.category} onChange={(v) => set("category", v)} prefix="pulse" /></div>
        {shared && <div className="mt-8"><p className="mb-2 text-sm font-semibold text-emerald-700">Shared with you</p><PulseCard s={shared} open /></div>}
        <p className="mt-8 text-sm text-slate-600" data-testid="pulse-count">
          {data ? `${data.total} actionable ${data.total === 1 ? "story" : "stories"} from the last two weeks` : "Loading…"}
          {meta?.pending > 0 && <span className="ml-2 text-xs">· {meta.pending} new headlines still being reviewed</span>}
        </p>
        <div className="mt-4 space-y-4">
          {data === null && [...Array(4)].map((_, i) => <div key={i} className="h-40 animate-pulse rounded-3xl bg-white" />)}
          {data?.items.map((s) => <PulseCard key={s.id} s={s} province={f.province} />)}
          {data?.items.length === 0 && <p className="rounded-3xl border border-dashed border-slate-300 bg-white py-14 text-center text-slate-600" data-testid="pulse-empty">No actionable stories match yet — try another category or all of Canada.</p>}
        </div>
        {data?.has_more && <div className="mt-8 text-center"><button onClick={() => setLimit((n) => n + 20)} data-testid="pulse-more" className="rounded-full border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-800">Show more</button></div>}
        <p className="mt-10 text-xs text-slate-600">Stories are screened by AI to keep only items with a clear local action. AI can make mistakes — always check the original article.</p>
      </div>
    </div>
  );
}
